import React from 'react';
import TimerPill from '../timer/TimerPill';
import WaButton from '../../../../components/wa/WaButton';
import './LessonTopbar.css';

/** Initials for the avatar, from a full name. */
const initials = (name = '') =>
	name
		.trim()
		.split(/\s+/)
		.slice(0, 2)
		.map((w) => w[0]?.toUpperCase() || '')
		.join('') || '·';

/**
 * Lesson topbar (phone base, extended at desktop) — surface-card with a 1px
 * bottom line: the identity block (avatar, student name + sub, a chevron),
 * the timer pill, and the filled Recap button. On the linked-student happy
 * path the sub reads "Tue 4:30 · Voice 30 min".
 *
 * THE IDENTITY BLOCK IS THE STUDENT DRAWER'S TRIGGER (design pass 2, change
 * 6, 2026-09-21). Until batch B a 40px ⋯ button at the far right opened the
 * drawer and the name beside the avatar did nothing, which is the wrong way
 * round: the drawer is ABOUT the student, so the student is the control. The
 * whole block is one 44px button with a chevron that says it opens; the ⋯ is
 * gone. Everything the ⋯ reached is still in that drawer (Add a task, Full
 * student profile, Student didn't come, link a student) and
 * the Student snapshot the rail used to repeat lives there alone now.
 *
 * RECAP IS REACHABLE FROM HERE AT EVERY WIDTH AND ON EVERY TAB (Anthony,
 * 2026-09-13: "accessible from the start of the lesson in a more obvious
 * place — right now they are hidden or below something else"). Design pass 2
 * change 5 made it the one filled control in the bar: a `variant="brand"`
 * Web Awesome button, icon plus the word "Recap" at EVERY width, and a second
 * line that reports the drawer's autosave state ("Not started", "Saved 2 min
 * ago"). Change 7 then removed the other two Recap entry points (the right
 * column's disclosure and the tool-row button), so this button and the Last
 * lesson row are the only routes into the drawer; recapEntryPoints.test.js
 * pins the count. The label and the state are LIGHT DOM text so tests and the
 * fixture can read them.
 *
 * PRIVATE (design pass 4 P2.2, 2026-10-04) sits after the timer and before
 * Recap: lock + "Private" + the entry count ("14 notes"), lock + count alone
 * on phone, 44px, filled ink while the file is open. It is a plain <button>,
 * not a wa-button, because its look is the Private file's ink grammar rather
 * than a WA variant, and its `aria-pressed` must be a real attribute. What it
 * opens is the page's decision (the rail's History at 1200+, a sheet wherever
 * the rail is hidden or too narrow); this bar only reports the click.
 *
 * COMPLETE LESSON IS NOT HERE ANY MORE (change 10). The desktop-only 40px
 * primary button that used to sit beside Recap moved into the Recap drawer's
 * footer, which is where the writing it completes actually is; from any tab
 * at any width the route is Recap, then Complete lesson, and the Wrap-up
 * confirm it opens is unchanged.
 */
const LessonTopbar = ({
	studentName,
	subLine,
	timer,
	onStartTimer,
	onOpenDrawer,
	onOpenRecap,
	recapSaveState = 'Not started',
	onOpenPrivate = null,
	privateCount = null,
	privateOpen = false,
}) => (
	<header className="ld-topbar">
		<button
			type="button"
			className="ld-topbar__identity"
			aria-label={`${studentName || 'Unlinked lesson'}: student details and lesson actions`}
			onClick={onOpenDrawer}>
			<span className="ld-topbar__avatar" aria-hidden="true">
				{initials(studentName)}
			</span>
			<span className="ld-topbar__id">
				<span className="ld-topbar__name">{studentName || 'Unlinked lesson'}</span>
				{subLine && <span className="ld-topbar__sub">{subLine}</span>}
			</span>
			<i className="fa-sharp fa-solid fa-chevron-down ld-topbar__chev" aria-hidden="true" />
		</button>
		<TimerPill timer={timer} onStart={onStartTimer} />
		{onOpenPrivate && (
			<button
				type="button"
				className={`ld-topbar__private${privateOpen ? ' is-on' : ''}`}
				aria-pressed={privateOpen}
				aria-label={`Private file${privateCount != null ? `, ${privateCount} ${privateCount === 1 ? 'note' : 'notes'}` : ''}`}
				onClick={onOpenPrivate}>
				<i className="fa-sharp fa-solid fa-lock" aria-hidden="true" />
				<span className="ld-topbar__private-lab">
					<span className="ld-topbar__private-word">Private</span>
					{privateCount != null && (
						<span className="ld-topbar__private-count">
							{privateCount}
							<span className="ld-topbar__private-word"> {privateCount === 1 ? 'note' : 'notes'}</span>
						</span>
					)}
				</span>
			</button>
		)}
		{onOpenRecap && (
			<WaButton
				variant="brand"
				// `class`, not `className`: React 18 writes `className` on a custom
				// element as the attribute "classname", which no stylesheet matches
				// (QuickAddTaskDialog's <wa-dialog> does the same).
				class="ld-topbar__recap"
				data-guide="lesson-recap"
				aria-label="Lesson recap"
				title="Lesson recap: Everyone, Parents, Teachers only"
				onClick={onOpenRecap}>
				<i slot="start" className="fa-sharp fa-solid fa-file-pen" aria-hidden="true" />
				<span className="ld-topbar__recaptext">
					<span className="ld-topbar__recaplabel">Recap</span>
					<span className="ld-topbar__recapstate">{recapSaveState}</span>
				</span>
			</WaButton>
		)}
	</header>
);

export default LessonTopbar;
